import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";

export default function Login() {
  const { user, login, registrationOpen, formatApiError } = useAuth();
  const [email, setEmail] = useState("admin@realtorcrm.app");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (user && user !== false) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Signed in");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error("Sign in failed", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-slate-50">
      <div className="hidden lg:flex lg:flex-col lg:w-1/2 bg-white border-r border-slate-200 px-12 py-12 justify-between">
        <div className="flex items-center gap-2">
          <div
            className="h-10 w-10 rounded-md flex items-center justify-center text-white font-bold"
            style={{ background: "hsl(var(--brand))" }}
          >
            RC
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900 font-display">
              Realtor Outreach CRM
            </div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">
              Brokerage &amp; agent network
            </div>
          </div>
        </div>
        <div className="max-w-md">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 font-display">
            Build the realtor network.
          </h1>
          <p className="mt-4 text-slate-600 text-sm leading-relaxed">
            One place to find brokerages in your area, scrape their agents,
            enrich social profiles, and export polished vCards straight to
            your phone.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-slate-600">
            <Bullet>Discover real brokerages — manual entry always available.</Bullet>
            <Bullet>Scrape agent rosters with photos and contact details.</Bullet>
            <Bullet>Per-realtor social discovery (Facebook, Instagram, LinkedIn, TikTok).</Bullet>
            <Bullet>vCard exports that import cleanly into iOS Contacts.</Bullet>
          </ul>
        </div>
        <div className="text-xs text-slate-400">
          © {new Date().getFullYear()} Realtor Outreach CRM
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-sm bg-white rounded-lg border border-slate-200 p-6 sm:p-8"
          data-testid="login-form"
        >
          <div className="flex items-center gap-2 mb-6 lg:hidden">
            <div
              className="h-9 w-9 rounded-md flex items-center justify-center text-white font-bold"
              style={{ background: "hsl(var(--brand))" }}
            >
              RC
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900 font-display">
                Realtor Outreach CRM
              </div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Sign in</div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 font-display">
            Sign in
          </h2>
          <p className="text-sm text-slate-500 mt-1">Use your admin credentials.</p>

          <div className="space-y-4 mt-6">
            <div>
              <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Email
              </Label>
              <Input
                id="email"
                type="text"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 min-h-[44px]"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Password
              </Label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 min-h-[44px]"
                data-testid="login-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy}
            className="w-full mt-6 min-h-[44px] text-white"
            style={{ background: "hsl(var(--brand))" }}
            data-testid="login-submit-button"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Sign in
          </Button>

          {registrationOpen && (
            <p className="text-sm text-slate-500 mt-4 text-center">
              No accounts yet?{" "}
              <a className="text-[hsl(var(--brand-strong))] font-medium underline-offset-4 hover:underline" href="/register">
                Register the first admin
              </a>
            </p>
          )}

          <div className="mt-6 flex items-center gap-2 text-xs text-slate-400">
            <ShieldCheck className="h-3.5 w-3.5" />
            Sessions stored locally only — no third-party cookies.
          </div>
        </form>
      </div>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex gap-2">
      <span
        className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
        style={{ background: "hsl(var(--brand))" }}
      />
      <span>{children}</span>
    </li>
  );
}
